import { SapDocument } from '@/api/documents/documents.types.ts';
import Box from '@/components/base/box.component';
import CardContent from '@/components/base/card/card-content.component';
import CardHeader from '@/components/base/card/card-header.component';
import Card from '@/components/base/card/card.component';
import Container from '@/components/base/container.component';
import { InfoPair } from '@/components/base/info-pair.component';
import { SimulatedProgress } from '@/components/simulated-progress.component';
import { formatMoneyString } from '@/utils/format-money-string';
import { Receipt as ReceiptIcon } from '@mui/icons-material';
import { Grid, Link, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';

export default function OrderOverview({
  order,
  isPending,
}: {
  order: SapDocument | null;
  isPending: boolean;
}) {
  const shippingExpense = order?.documentAdditionalExpenses?.find(
    (expense) => expense?.expenseCode === 2,
  );
  if (isPending || !order) {
    return (
      <Container
        sx={{
          width: '100%',
          mx: 'auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <SimulatedProgress
          title="Lade Auftragsübersicht"
          description="Dies kann einige Sekunden dauern"
        />
      </Container>
    );
  }

  return (
    <Card elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
      <CardHeader
        icon={<ReceiptIcon />}
        title={`Auftragsübersicht: ${order.docNum}`}
      />
      <CardContent>
        <Grid container spacing={0}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <InfoPair label="Auftragsnummer" value={order.docNum} />
            <InfoPair
              label="Auftragsdatum"
              value={dayjs(order.docDate).format('DD.MM.YYYY')}
            />
            {order?.numAtCard && (
              <InfoPair label={'Bestellnummer'} value={order.numAtCard} />
            )}
            {order.uTrackingNr && (
              <InfoPair label={'Tracking-Nummer'} value={order.uTrackingNr} />
            )}
            {order.uLinkTracking && (
              <InfoPair
                label={'Tracking-Link'}
                value={
                  <Link href={order.uLinkTracking} target="_blank">
                    {order.uLinkTracking}
                  </Link>
                }
              />
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <InfoPair label={'Kundennummer'} value={order.cardCode} />
            <InfoPair label={'Kundenname'} value={order.cardName} />
            {shippingExpense && (
              <InfoPair
                label={'Versandkosten'}
                value={formatMoneyString(shippingExpense.lineTotal)}
              />
            )}
            {order.uLieferscheinNrBvw && (
              <InfoPair
                label={'Lieferscheinnummer'}
                value={order.uLieferscheinNrBvw}
              />
            )}
          </Grid>
        </Grid>
        <Stack
          sx={{
            width: '100%',
            justifyContent: 'space-between',
          }}
          direction={'row'}
          spacing={4}
        >
          <Box
            sx={{
              width: '100%',
              m: '0 !important',
            }}
          >
            <Typography variant={'subtitle2'}>Rechnungsanschrift</Typography>
            <Typography
              sx={{
                whiteSpace: 'pre-line',
              }}
            >
              {order.address?.replace(/\r/g, '\n')}
            </Typography>
          </Box>
          <Box
            sx={{
              width: '100%',
              m: '0 !important',
            }}
          >
            <Typography variant={'subtitle2'}>Lieferanschrift</Typography>
            <Typography
              sx={{
                whiteSpace: 'pre-line',
              }}
            >
              {order.address2?.replace(/\r/g, '\n')}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
